import { __ } from '@wordpress/i18n';
import { MODAL_OPTIONS } from '../../constants/modalOptions';
import FieldOptions from './FieldOptions';
import ModalHeader from './ModalHeader';
import ModalOverlay from './ModalOverlay';

// Import AREA_TO_SECTION_ID
import { AREA_TO_SECTION_ID } from '../../constants/areaMappings';

const FieldModal = ({ 
  isOpen, 
  area, 
  editingField, 
  onClose, 
  onFieldSelect,
  cardSections
}) => {
  if (!isOpen) return null;

  // Get all modal options for this area
  const allModalOptions = area ? MODAL_OPTIONS[area] || [] : [];

  // Get used field types in the current area
  const getUsedFieldTypes = () => {
    if (!area || !cardSections) return [];
    
    // Find the section ID for this area
    const sectionId = Object.entries(AREA_TO_SECTION_ID).find(
      ([areaKey, sectionId]) => areaKey === area
    )?.[1];
    
    if (!sectionId) return [];
    
    const section = cardSections.find(s => s.id === sectionId);
    if (!section) return [];
    
    return section.fields.map(field => field.type);
  };

  const usedFieldTypes = getUsedFieldTypes();
  
  // NEW: Updated logic for button restrictions
  const getAvailableModalOptions = () => {
    if (editingField) {
      // EDIT MODE: Show only unused fields (excluding the current field being edited)
      // Special handling for button types
      if (editingField.type === 'view-btn' || editingField.type === 'schedule-tour-button') {
        // When editing a button, show all options including the other button type
        return allModalOptions.filter(option => 
          !usedFieldTypes.some(usedType => 
            usedType === option.id && option.id !== editingField.type
          )
        );
      } else {
        // Normal edit mode for non-button fields
        return allModalOptions.filter(option => 
          !usedFieldTypes.some(usedType => 
            usedType === option.id && option.id !== editingField.type
          )
        );
      }
    } else {
      // ADD MODE: Normal logic with button restrictions
      // Check if a button already exists in footer areas
      const hasButton = usedFieldTypes.some(type => 
        type === 'view-btn' || type === 'schedule-tour-button'
      );
      
      // Apply button restriction only for footer areas
      if ((area === 'footer-left' || area === 'footer-right') && hasButton) {
        // If button exists in footer area, hide both button options
        return allModalOptions.filter(option => 
          option.id !== 'view-btn' && 
          option.id !== 'schedule-tour-button' && 
          !usedFieldTypes.includes(option.id)
        );
      }
      
      // For thumbnail area, check if image or carousel already exists
      if (area === 'thumbnail') {
        const hasImageType = usedFieldTypes.some(type => 
          type === 'image' || type === 'carousel'
        );
        
        if (hasImageType) {
          // If image type exists, hide both image and carousel options
          return allModalOptions.filter(option => 
            option.id !== 'image' && option.id !== 'carousel' && !usedFieldTypes.includes(option.id)
          );
        }
      }
      
      // No restrictions apply, show all available options
      return allModalOptions.filter(option => !usedFieldTypes.includes(option.id));
    }
  };

  const availableModalOptions = getAvailableModalOptions();

  // NEW: Check if thumbnail has image type for special message
  const hasThumbnailImageType = area === 'thumbnail' && usedFieldTypes.some(type => 
    type === 'image' || type === 'carousel'
  );

  // Check if footer area already has a button
  const hasFooterButton = (area === 'footer-left' || area === 'footer-right') && 
    usedFieldTypes.some(type => type === 'view-btn' || type === 'schedule-tour-button');

  // Get modal title based on mode
  const getModalTitle = () => {
    if (editingField) {
      return __('Replace Field:', 'havenlytics') + ` ${editingField.label}`;
    } else {
      return __('Insert Element -', 'havenlytics') + ` ${area}`;
    }
  };

  const handleFieldSelect = (option) => {
    onFieldSelect(option);
    // Close modal after selection in edit mode
    if (editingField) {
      onClose();
    }
    // Don't close modal automatically after selection in add mode
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="hvnly--builder---api_property__builder___option_card">
        <ModalHeader 
          title={getModalTitle()} 
          onClose={onClose} 
        />
        <div className="hvnly--builder---api_property__builder___option_card_body">
          {/* Edit mode info message */}
          {editingField && (
            <div className="hvnly--builder---api_property__builder___edit_mode_info">
              <i className="fas fa-exchange-alt"></i>
              <span>{__('Select a field type to replace', 'havenlytics')} "{editingField.label}"</span>
            </div>
          )}
          
          {/* NEW: Special message for thumbnail area */}
          {area === 'thumbnail' && hasThumbnailImageType && !editingField && (
            <div className="hvnly--builder---api_property__builder___thumbnail_restriction_message">
              <i className="fas fa-info-circle"></i>
              <span>{__('Only one image type allowed. Remove current image to select a different type.', 'havenlytics')}</span>
            </div>
          )}
          
          {/* NEW: Special message for footer areas with button restriction */}
          {(area === 'footer-left' || area === 'footer-right') && hasFooterButton && !editingField && (
            <div className="hvnly--builder---api_property__builder___button_restriction_message">
              <i className="fas fa-info-circle"></i>
              <span>{__('Only one button allowed in this area. Remove current button to select a different one.', 'havenlytics')}</span>
            </div>
          )}
          
          {availableModalOptions.length > 0 ? (
            <FieldOptions 
              options={availableModalOptions}
              onSelect={handleFieldSelect}
            />
          ) : (
            <div className="hvnly--builder---api_property__builder___no_options">
              <i className="fas fa-check-circle"></i>
              <p>
                {editingField 
                  ? __("No available fields to replace with. All other field types are already used in this area.", "havenlytics")
                  : area === 'thumbnail' && hasThumbnailImageType 
                    ? __("Image type already selected. Remove it to choose a different type.", "havenlytics")
                    : (area === 'footer-left' || area === 'footer-right') && hasFooterButton
                      ? __("Button already selected. Remove it to choose a different button type.", "havenlytics")
                      : __("All available fields have been added to this area", "havenlytics")
                }
              </p>
              <small>{__("Remove some fields to see more options", "havenlytics")}</small>
            </div>
          )}

          {/* NEW: Cancel button for edit mode */}
          {editingField && (
            <div className="hvnly--builder---api_property__builder___field_editor_actions">
              <button 
                className="hvnly--builder---api_property__builder___field_editor_btn hvnly--builder---api_property__builder___field_editor_cancel_btn"
                onClick={onClose}
              >
                {__('Cancel', 'havenlytics')}
              </button>
            </div>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
};

export default FieldModal;